/**
 * What §3's 5-step probe records about an instance — the result of `instance/probe.ts` now,
 * and before that one hand-verified field.
 *
 * §2 trap 1 is why the caption prefix was the first member and why it is *configuration* rather than a
 * string literal in the parsers: `/api/v1/captions/{id}?label=…` returns **200 with a zero-byte body**
 * on this instance while `/companion/api/v1/captions/…` returns real WebVTT. Nothing in the JSON
 * distinguishes them — only fetching the bytes does.
 */
export type CaptionPathPrefix = '/companion/api/v1/captions' | '/api/v1/captions';

/**
 * Three-valued on purpose, for every question the probe can fail to answer.
 *
 * `unknown` is not a synonym for "no": an instance whose `/api/v1/popular` timed out is a different
 * thing from one whose administrator switched it off, and only the second is worth greying a tab out
 * for with a reason. §8 asks to "grey out Popular **with a reason** if the instance disabled it", which
 * requires knowing that it *was* disabled.
 */
export type CapabilityState = 'serves' | 'disabled' | 'unknown';

export interface InstanceCapabilities {
  /** `null` — neither path returned a non-empty body; hide the CC button entirely. */
  readonly captionPathPrefix: CaptionPathPrefix | null;
  /** `/api/v1/popular`. The one endpoint Invidious has a documented switch for (`popular_enabled`). */
  readonly popular: CapabilityState;
  /** `/api/v1/trending`. Not switchable in Invidious, but it can fail for a region (`feed/region.ts`). */
  readonly trending: CapabilityState;
  /** `adaptiveFormats` on the gate video — no adaptive ladder means no DASH and no quality menu. */
  readonly adaptiveFormats: CapabilityState;
  /**
   * The DASH manifest **fetched**, not merely present in the payload — `dashUrl` plus §2's mandatory
   * `?local=true`, checked for a non-empty body that is actually an MPD.
   *
   * Fetching it is the correction 3e made to its own first design. The obvious reading is the record's
   * `adaptiveFormats[].url`: point it at googlevideo and the instance is not proxying. But that field
   * is direct googlevideo **by design** on a plain `/api/v1/videos/{id}` — which is the request the app
   * makes — and the app never fetches those URLs at all: `parse/video.ts` builds `dashUrl?local=true`
   * and `/latest_version?id=…&itag=18&local=true` instead. A probe that judged proxying from the
   * payload would therefore refuse every healthy instance.
   */
  readonly dashManifest: CapabilityState;
  /**
   * `/api/v1/auth/*` answers at all — checked **unauthenticated**, so it says the subsystem is present
   * rather than that any particular endpoint is. Measured (3e): every path under `/api/v1/auth/`,
   * including one that does not exist, answers `403 {"error":"Request must be authenticated"}` with no
   * credential, because the auth check runs *before* routing. That is the same ordering 3d saw from the
   * other side, where a valid token turned a bad path into `403 Requested path … was not found.`
   */
  readonly auth: CapabilityState;
  /** `{name, version}` from `/api/v1/stats`, or `null` when it did not answer with a software block. */
  readonly software: InstanceSoftware | null;
}

export interface InstanceSoftware {
  readonly name: string;
  readonly version: string;
  readonly branch: string | null;
}

/**
 * The verified-working configuration for `invidious.example.com` — every field measured, not guessed.
 *
 * It stays the *starting* value rather than becoming an empty set now that there is a probe: the app
 * boots before the probe can have run (§3 step 3 is a 97 KB request), and booting into
 * `captionPathPrefix: null` would hide the CC button on the first video every time. A probe result
 * replaces it wholesale.
 */
export const DEFAULT_CAPABILITIES: InstanceCapabilities = {
  captionPathPrefix: '/companion/api/v1/captions',
  popular: 'serves',
  trending: 'serves',
  adaptiveFormats: 'serves',
  dashManifest: 'serves',
  auth: 'serves',
  software: null,
};

/** What a probe starts from: nothing is known, so nothing is claimed. */
export const UNKNOWN_CAPABILITIES: InstanceCapabilities = {
  captionPathPrefix: null,
  popular: 'unknown',
  trending: 'unknown',
  adaptiveFormats: 'unknown',
  dashManifest: 'unknown',
  auth: 'unknown',
  software: null,
};

/** The prefix the API *claims* in `captions[].url` — and the one that is silently broken. */
export const DOCUMENTED_CAPTION_PREFIX = '/api/v1/captions';

/** The prefix that actually returns WebVTT bytes. */
export const COMPANION_CAPTION_PREFIX = '/companion/api/v1/captions';
